/**
 * Document capture — Stripe/MetaMap-style full-bleed camera with a card frame
 * and shutter button. One side of one document (./documents): a Thai ID card or
 * a driving licence, front or back, or a passport's data page.
 *
 * Prefers the rear camera (documents sit flat on a surface); falls back to the
 * front camera when `environment` is unavailable. Capture uploads a file ref
 * then fires that document's event (`ID_FRONT_CAPTURED`, `PASSPORT_CAPTURED`, …)
 * with the ref as payload.
 */
import { grabFrontFrame } from './silentCapture';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AttemptStepKey } from './attemptReason';
import { coverCropRect, grabFrame, submitCapturedDocument, type DocumentSide } from './capture';
import { CollectionFlowError, postCollectionFlowEvent } from './client';
import { detectCapture, type CaptureDetection } from './detectCapture';
import type { DocumentKind } from './documents';
import { reportAttempt } from './reportAttempt';
import type { CollectionFlowSnapshot } from './types';
import { StatusGlyph } from '../ui/StatusGlyph';

type Phase = 'starting' | 'ready' | 'captured' | 'submitting' | 'denied';

interface CaptureDocumentProps {
  token: string;
  document: DocumentKind;
  side: DocumentSide;
  /** Issuing country the applicant chose (./DocumentChoice), when they chose one. */
  country?: string;
  /** The statechart leaf this capture belongs to — what attempts are reported under. */
  stepKey: AttemptStepKey;
  /** Take a front-camera frame after the shot (the step's silent capture setting). */
  silentCapture?: boolean;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
  /**
   * Escape hatch offered when the camera cannot start. Present only when there
   * is somewhere to fall back TO — i.e. a desktop that opted out of the phone
   * handoff and can still return to the QR. Absent on the phone itself, where
   * the handoff has already happened and there is no other device to suggest.
   *
   * Necessary because `denied` cannot distinguish "permission refused" (the
   * applicant can fix it) from "this machine has no camera" (they cannot). The
   * second is otherwise a dead end.
   */
  onCameraUnavailable?: () => void;
}

export function CaptureDocument({
  token,
  document,
  side,
  country,
  stepKey,
  silentCapture = false,
  onAdvance,
  onCameraUnavailable,
}: CaptureDocumentProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Per-committed-shot counter — bumped each time the customer takes a photo.
  // Used as the attempt idempotency key so a blocked-submit + retake on the SAME
  // shot collapse to one reported attempt (see reportAttempt dedup).
  const captureNonce = useRef(0);
  const [phase, setPhase] = useState<Phase>('starting');
  const [base64, setBase64] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Advisory capture detection (frame-align / face / laser) — see detectCapture.
  const [detection, setDetection] = useState<CaptureDetection | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          });
        } catch {
          // Desktop / no rear camera — front is fine for demos.
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await Promise.resolve(videoRef.current.play()).catch(() => undefined);
        }
        setPhase('ready');
      } catch {
        setPhase('denied');
      }
    }
    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const guide = guideRef.current;
    if (!video || !canvas) return;
    try {
      // Crop to exactly the on-screen card guide so the saved photo matches what
      // the user framed. The preview is `object-fit: cover`, so we invert that
      // transform from the guide's rendered rect into intrinsic sensor pixels.
      // If the guide isn't mounted (shouldn't happen in the live phase), fall
      // back to the full frame rather than throwing.
      let crop;
      if (guide && video.videoWidth > 0 && video.videoHeight > 0) {
        const vb = video.getBoundingClientRect();
        const gb = guide.getBoundingClientRect();
        crop = coverCropRect(
          video.videoWidth,
          video.videoHeight,
          vb.width,
          vb.height,
          gb.left - vb.left,
          gb.top - vb.top,
          gb.width,
          gb.height,
        );
      }
      // New committed shot → new idempotency scope for any attempt it produces.
      captureNonce.current += 1;
      setBase64(grabFrame(video, canvas, crop));
      // grabFrame left the captured (guide-cropped) image on the canvas — grade it
      // for frame-align / face / laser. Advisory: never blocks submit.
      setDetection(null);
      void detectCapture(canvas, side)
        .then(setDetection)
        .catch(() => setDetection(null));
      setError(null);
      setPhase('captured');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'capture failed');
    }
  }, [side]);

  const retake = useCallback(() => {
    // The customer rejected their own shot → one "retaken" attempt (client-side).
    reportAttempt(token, {
      stepKey,
      stepKind: 'document',
      reason: 'retaken',
      origin: 'client',
      attemptKey: `${stepKey}:${captureNonce.current}`,
    });
    setBase64(null);
    setError(null);
    setDetection(null);
    setPhase('ready');
  }, [token, stepKey]);

  const submit = useCallback(async () => {
    if (!base64) return;
    setPhase('submitting');
    setError(null);
    try {
      // A phone browser runs one camera at a time: stop the rear one first.
      let frontFrame: string | null = null;
      if (silentCapture) {
        stopCamera();
        frontFrame = await grabFrontFrame();
      }
      const next = await submitCapturedDocument(
        token,
        base64,
        { kind: document, side, country },
        postCollectionFlowEvent,
        frontFrame,
      );
      stopCamera();
      onAdvance(next);
    } catch (e) {
      // The server rejected this committed shot → one "server_rejected" attempt.
      const status = e instanceof CollectionFlowError ? e.status : undefined;
      reportAttempt(token, {
        stepKey,
        stepKind: 'document',
        reason: 'server_rejected',
        origin: 'server',
        attemptKey: `${stepKey}:${captureNonce.current}`,
        ...(status !== undefined ? { meta: { status } } : {}),
      });
      setError(status !== undefined ? `${status}` : 'submit failed');
      setPhase('captured');
    }
  }, [base64, token, document, side, country, stepKey, onAdvance, stopCamera, silentCapture]);

  // Document-specific on purpose: the copy names exactly what this step's event
  // can process — a generic "your document" told applicants to photograph
  // documents the flow could not use.
  const copyKey =
    document === 'thai_id'
      ? `flow.document.${side}`
      : document === 'passport'
        ? 'flow.document.passport'
        : `flow.document.drivingLicense.${side}`;
  const title = t(`${copyKey}.title`);
  const hint = t(`${copyKey}.hint`);

  if (phase === 'denied') {
    return (
      <section className="doc-capture doc-capture--denied" aria-label={title}>
        <StatusGlyph kind="error" />
        <p role="alert" className="alert">
          {t('flow.document.denied', {
            defaultValue: 'Camera access is required to continue.',
          })}
        </p>
        {onCameraUnavailable ? (
          <button
            type="button"
            className="btn"
            onClick={onCameraUnavailable}
            data-track="use_phone_instead"
            data-testid="use-phone-instead"
          >
            {t('flow.camera.usePhoneInstead', { defaultValue: 'Use my phone instead' })}
          </button>
        ) : null}
      </section>
    );
  }

  // Review freeze-frame before upload
  if (phase === 'captured' || phase === 'submitting') {
    return (
      <section
        className="doc-capture doc-capture--review"
        aria-label={title}
        data-testid="doc-review"
      >
        <p className="doc-review-label">
          {t('flow.document.reviewTitle', { defaultValue: 'Check your photo' })}
        </p>
        {base64 ? (
          <div className="doc-review-frame">
            <img
              src={`data:image/jpeg;base64,${base64}`}
              alt={t('flow.document.previewAlt', { defaultValue: 'Captured document' })}
              className="doc-review-img"
            />
          </div>
        ) : null}
        <DetectionChips detection={detection} document={document} side={side} />
        {error ? (
          <p role="alert" className="alert">
            {error}
          </p>
        ) : null}
        <div className="doc-actions">
          {phase === 'submitting' ? (
            <p className="step-text">
              {t('flow.document.submitting', { defaultValue: 'Submitting…' })}
            </p>
          ) : (
            <>
              <button type="button" className="btn btn--ghost" onClick={retake} data-testid="doc-retake" data-track="retake_photo">
                {t('flow.document.retake', { defaultValue: 'Retake' })}
              </button>
              <button type="button" className="btn" onClick={() => void submit()} data-testid="doc-submit" data-track="use_photo">
                {t('flow.document.usePhoto', { defaultValue: 'Use this photo' })}
              </button>
            </>
          )}
        </div>
        <canvas ref={canvasRef} hidden data-testid="doc-canvas" />
      </section>
    );
  }

  return (
    <section
      className="doc-capture doc-capture--live"
      aria-label={title}
      data-testid="doc-live"
    >
      <div className="doc-stage">
        <video
          ref={videoRef}
          className="doc-video"
          playsInline
          muted
          data-testid="doc-video"
        />
        {/* Dark vignette with a card-shaped cutout */}
        <div className="doc-mask" aria-hidden="true" />
        <div
          className={`doc-card-guide${document === 'passport' ? ' doc-card-guide--passport' : ''}`}
          aria-hidden="true"
          ref={guideRef}
        >
          <span className="doc-card-corner doc-card-corner--tl" />
          <span className="doc-card-corner doc-card-corner--tr" />
          <span className="doc-card-corner doc-card-corner--bl" />
          <span className="doc-card-corner doc-card-corner--br" />
        </div>

        <div className="doc-copy">
          <h1 className="doc-title">{title}</h1>
          <p className="doc-hint">{hint}</p>
        </div>

        <div className="doc-shutter-wrap">
          {phase === 'ready' ? (
            <button
              type="button"
              className="doc-shutter"
              data-track="take_photo"
              onClick={capture}
              aria-label={t('flow.document.capture', { defaultValue: 'Capture' })}
              data-testid="doc-shutter"
            >
              <span className="doc-shutter-inner" />
            </button>
          ) : (
            <p className="doc-starting">
              {t('flow.document.starting', { defaultValue: 'Starting camera…' })}
            </p>
          )}
        </div>
      </div>
      <canvas ref={canvasRef} hidden data-testid="doc-canvas" />
    </section>
  );
}

/**
 * Advisory capture-detection chips on the review screen. Non-blocking by design:
 * 'skipped' signals render nothing and the applicant can always submit — these
 * only nudge a retake when the shot looks wrong for the side being captured.
 */
function DetectionChips({
  detection,
  document,
  side,
}: {
  detection: CaptureDetection | null;
  document: DocumentKind;
  side: DocumentSide;
}) {
  const { t } = useTranslation();
  if (!detection) return null;

  const chips: Array<{ key: string; label: string; tone: 'info' | 'ok' | 'warn' }> = [];

  const frameOk = detection.frameAlign === 'ok';
  chips.push({
    key: 'frame',
    tone: frameOk ? 'ok' : 'warn',
    label: frameOk
      ? t('flow.document.detect.frameOk', { defaultValue: 'Fits the frame' })
      : t('flow.document.detect.frameFail', { defaultValue: 'Fit it fully inside the frame' }),
  });

  if (side === 'front' && detection.face !== 'skipped') {
    const faceOk = detection.face === 'ok';
    chips.push({
      key: 'face',
      tone: faceOk ? 'ok' : 'warn',
      label: faceOk
        ? t('flow.document.detect.faceOk', { defaultValue: 'Photo detected' })
        : document === 'passport'
          ? t('flow.document.detect.faceFailPassport')
          : t('flow.document.detect.faceFail', {
              defaultValue: 'No portrait found — is this the front?',
            }),
    });
  }

  // Only the Thai ID card's back carries a laser code.
  if (document === 'thai_id' && side === 'back' && detection.laser === 'deferred') {
    chips.push({
      key: 'laser',
      tone: 'info',
      label: t('flow.document.detect.laserDeferred', {
        defaultValue: 'Laser code checked after upload',
      }),
    });
  }

  return (
    <ul className="doc-detect" aria-live="polite">
      {chips.map((c) => (
        <li key={c.key} className={`doc-detect-chip doc-detect-chip--${c.tone}`}>
          {c.label}
        </li>
      ))}
    </ul>
  );
}
